import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, ShieldCheck, Sparkles, CheckCircle2, Search, Loader2 } from 'lucide-react';
import PasswordStrengthMeter, { isPasswordValid } from '../components/PasswordStrengthMeter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SignUp = () => {
  const { user, loading: authLoading, companySignup } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    company_name: '',
    admin_name: '',
    email: '',
    password: '',
    phone: '',
    dot_number: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [dotLoading, setDotLoading] = useState(false);
  const [dotResult, setDotResult] = useState(null);
  const [dotError, setDotError] = useState('');

  useEffect(() => {
    if (!authLoading && user) navigate('/dashboard', { replace: true });
  }, [user, authLoading, navigate]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const lookupDot = async () => {
    setDotError(''); setDotResult(null);
    const dot = (form.dot_number || '').trim();
    if (!/^\d+$/.test(dot)) { setDotError('Enter a numeric US DOT number.'); return; }
    setDotLoading(true);
    try {
      const res = await axios.get(`${API}/fmcsa/carrier/${dot}`);
      const d = res.data;
      setDotResult(d);
      // Auto-fill blank fields from FMCSA (never overwrite what the user typed).
      setForm((prev) => ({
        ...prev,
        company_name: prev.company_name || d.legal_name || '',
        phone: prev.phone || d.telephone || '',
      }));
    } catch (e) {
      const code = e.response?.status;
      setDotError(code === 404 ? `No carrier found for DOT #${dot}.`
        : code === 503 ? 'FMCSA lookup is unavailable right now — you can fill in details manually.'
        : (e.response?.data?.detail || 'Lookup failed.'));
    } finally { setDotLoading(false); }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isPasswordValid(form.password)) {
      setError('Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number, and a special symbol.');
      return;
    }
    setLoading(true);
    const result = await companySignup(form);
    setLoading(false);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error || 'Sign-up failed');
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="signup-page">
      {/* LEFT — form */}
      <div className="w-full lg:w-1/2 flex flex-col p-6 sm:p-10 bg-white">
        <div className="max-w-md mx-auto w-full flex-1 flex flex-col justify-center">
          <Link
            to="/"
            data-testid="signup-back-link"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-8 transition-colors w-fit"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2} />
            Back to home
          </Link>

          <div className="mb-8">
            <img src="/assets/civilx-tms-logo.png" alt="CivilX TMS" className="h-10 w-auto mb-7" />
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Create your company
            </h1>
            <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Free to start. You'll be set up as the Company Admin and can invite drivers right away.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            {error && (
              <div data-testid="signup-error" className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {error}
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Company name
              </label>
              <input
                name="company_name"
                value={form.company_name}
                onChange={onChange}
                required
                data-testid="signup-company-name"
                placeholder="Acme Trucking LLC"
                className="w-full px-4 py-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent focus:outline-none transition-all"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                US DOT number (optional)
              </label>
              <div className="flex gap-2">
                <input
                  name="dot_number"
                  value={form.dot_number}
                  onChange={(e) => setForm({ ...form, dot_number: e.target.value.replace(/\D/g, '') })}
                  data-testid="signup-dot"
                  placeholder="e.g. 44110"
                  className="flex-1 px-4 py-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent focus:outline-none transition-all"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                />
                <button
                  type="button"
                  onClick={lookupDot}
                  disabled={dotLoading || !form.dot_number}
                  data-testid="signup-dot-lookup"
                  className="px-4 py-3 rounded-md border border-[#002FA7] text-[#002FA7] font-semibold hover:bg-[#002FA7] hover:text-white transition-all disabled:opacity-40 flex items-center gap-2"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  {dotLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  {dotLoading ? '…' : 'Look up'}
                </button>
              </div>
              {dotError && (
                <p data-testid="signup-dot-error" className="text-xs text-red-600 mt-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{dotError}</p>
              )}
              {dotResult && (
                <div data-testid="signup-dot-result" className="mt-2 text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <span className="font-semibold text-slate-900">{dotResult.legal_name}</span>
                  {' · '}{dotResult.operating_status}{dotResult.address ? ` · ${dotResult.address}` : ''}
                  <span className="block mt-0.5 text-slate-500">We pre-filled your company name — edit anything before continuing.</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  Your name
                </label>
                <input
                  name="admin_name"
                  value={form.admin_name}
                  onChange={onChange}
                  required
                  data-testid="signup-admin-name"
                  placeholder="Jane Doe"
                  className="w-full px-4 py-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent focus:outline-none transition-all"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  Phone (optional)
                </label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  data-testid="signup-phone"
                  placeholder="(555) 123-4567"
                  className="w-full px-4 py-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent focus:outline-none transition-all"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Work email
              </label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                required
                data-testid="signup-email"
                placeholder="you@company.com"
                className="w-full px-4 py-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent focus:outline-none transition-all"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Password
              </label>
              <input
                name="password"
                type="password"
                value={form.password}
                onChange={onChange}
                required
                minLength={8}
                data-testid="signup-password"
                placeholder="At least 8 characters"
                className="w-full px-4 py-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent focus:outline-none transition-all"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              />
              <PasswordStrengthMeter password={form.password} testid="signup-password-strength" />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="signup-submit"
              className="w-full bg-[#002FA7] text-white py-3.5 px-4 rounded-md font-semibold hover:bg-[#0034BD] focus:ring-2 focus:ring-[#002FA7] focus:ring-offset-2 focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {loading ? 'Creating your company…' : 'Create company & continue'}
            </button>

            <p className="text-xs text-slate-500 text-center pt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Already have an account?{' '}
              <Link to="/login" data-testid="signup-to-login-link" className="text-[#002FA7] font-semibold hover:underline">
                Sign in
              </Link>
            </p>
          </form>
        </div>
      </div>

      {/* RIGHT — visual */}
      <div
        className="hidden lg:flex lg:w-1/2 relative items-end p-12"
        style={{
          backgroundImage: 'url(/assets/hero-truck.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#04081f]/85 via-[#04081f]/55 to-[#04081f]/30" />
        <div className="relative z-10 max-w-lg">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.07] border border-white/10 backdrop-blur-sm mb-6">
            <Sparkles className="w-3.5 h-3.5 text-[#3D7DFF]" strokeWidth={2.5} />
            <span className="text-xs font-medium tracking-wide text-white/85" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              60-second setup
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-5 leading-[1.15]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Your fleet, dispatched and paid — without the spreadsheets.
          </h2>
          <ul className="space-y-3">
            {[
              'Invite drivers via tokenized links',
              'Create & assign loads in seconds',
              'Run weekly payroll automatically',
              'Live analytics + CSV exports',
            ].map((t) => (
              <li key={t} className="flex items-center gap-3 text-sm text-slate-100" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <CheckCircle2 className="w-4 h-4 text-[#3D7DFF]" strokeWidth={2.5} />
                {t}
              </li>
            ))}
          </ul>
          <div className="mt-8 flex items-center gap-2 text-xs text-slate-300/80" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <ShieldCheck className="w-4 h-4" strokeWidth={2} />
            Multi-tenant isolation · JWT + bcrypt · Your data is yours.
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
